import React, { useContext, useMemo, useState } from "react";
import classnames from "classnames";
import { compose } from "redux";
import { useHistory, useLocation } from "react-router-dom";
import logo from "assets/logo_t.svg";
import { Text, Logo, withBreakpoint, LayoutContext, BreakpointContext } from "components/common";
import { useExternalSupport } from "utils";
import { isSupport as checkSupport } from "routes/pageConfig";
import { hasPagePermission } from "routes/utils";
import { NAV_BAR_ITEMS } from "./consts";
import { parseInfo } from "./navHelper";

const iconSize = 24;

const NavbarItem = ({ item, index, isNavbarVisible, onClick, hoveredIndex, onHover }) => {
  const { Icon, iconName, title, key, navBarSettings } = item;
  const { forceCollapse } = useContext(LayoutContext);
  const { isBelow } = useContext(BreakpointContext);
  const showingWideMenu = (!forceCollapse && !isBelow("xl")) || isNavbarVisible;
  const isSupport = checkSupport(item);
  const { pathname } = useLocation();
  const isHovered = hoveredIndex === index;
  const { color } = parseInfo(key, pathname, "white", null, isHovered);

  return (
    <div
      role="presentation"
      className={classnames("flex p-3 cursor-pointer w-full mx-auto")}
      style={{
        borderColor: "#ddf0ed1f",
      }}
      onClick={onClick}
      onMouseOver={() => onHover()}
      onFocus={() => onHover()}
    >
      <div
        className={classnames("flex items-center", {
          "py-6": isSupport,
          "px-2 rounded bg-support hover:bg-primary text-sm w-full":
            isSupport && showingWideMenu,
        })}
        style={{ height: "28px" }}
      >
        <div className="mx-auto">
          {Icon && (
            <Icon
              color={color}
              style={{ width: iconSize }}
              className="float-left"
            />
          )}
          {iconName && (
            <i
              style={{ width: iconSize, color, fontSize: iconSize }}
              className={`float-left fas ${iconName} text-center`}
            />
          )}
          <Text
            weight="font-medium"
            color={color}
            className={classnames("pl-2 capitalize flex text-lg", {
              "hidden": !showingWideMenu,
              "text-lg": !isSupport,
              "text-sm": isSupport,
            })}
          >
            {navBarSettings?.desktopNav?.title || title || key}
          </Text>
        </div>
      </div>
    </div>
  );
};

const NavbarDesktop = ({
  breakpointContext: { isBelow },
  showMenu,
  onClick,
}) => {
  const { forceCollapse } = useContext(LayoutContext);
  const { push } = useHistory();
  const { openExternalSupport } = useExternalSupport();
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const supportItems = NAV_BAR_ITEMS.filter((i) => checkSupport(i));
  const supportItem = supportItems.length > 0 ? supportItems[0] : null;
  const showingWideMenu = (!forceCollapse && !isBelow("xl")) || showMenu;

  const memoizedVisibleItems = useMemo(() => {
    return NAV_BAR_ITEMS.filter((item) => {
      const hasPerms = hasPagePermission(item);
      if (!hasPerms) {
        return false;
      }
      if (item.navBarSettings?.desktopNav?.hide){
        return false;
      }
      return !checkSupport(item);
    });
  }, []);

  const renderLogo = () => {
    if (showingWideMenu){
      return(
        <Logo
          white
          className="w-8/12 mx-auto"
          style={{ justifyContent: "center" }}
        />
      );
    }
    return(
      <div className="py-4 pl-3 bg-black-secondary">
        <img
          src={logo}
          style={{ width: "auto", height: 36 }}
          className="w-1/2"
          alt="logo"
        />
      </div>
    );
  };

  const renderDrawerItem = (item, index) => {
    const { key, path } = item;
    const isSupport = checkSupport(item);
    const _onClick = () => {
      if (isSupport){
        openExternalSupport();
      } else {
        if (onClick) {
          onClick();
        }
        push(path);
      }
    };
    return (
      <div key={key} style={{ bottom: isSupport ? '10px' : 0 }}>
        <NavbarItem
          item={item}
          index={index}
          isNavbarVisible={showMenu}
          onClick={_onClick}
          hoveredIndex={hoveredIndex}
          onHover={() => setHoveredIndex(index)}
        />
      </div>
    );
  };

  return (
    <div
      className="flex flex-1 justify-center w-full h-screen"
    >
      <div className="flex flex-col w-full h-full justify-between">
        <button type="button" style={{ outline: 0 }} onClick={() => push("/")}>
          {renderLogo()}
        </button>
        <div className="flex-1" onMouseLeave={() => setHoveredIndex(null)}>
          {memoizedVisibleItems.map(renderDrawerItem)}
        </div>
        { supportItem && renderDrawerItem(supportItem, memoizedVisibleItems.length) }
      </div>
    </div>
  );
};

export default compose(withBreakpoint)(NavbarDesktop);
